import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import DesktopLayout from '../../../components/layout/DesktopLayout';
import api from '../../../services/api';
import PasswordResetModal from '../../../components/admin/PasswordResetModal';
import './index.css';

// Admin API 返回的會員資料結構
interface AdminMember {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  role: 'member' | 'moderator' | 'admin' | 'super_admin';
  plan: 'free' | 'basic' | 'premium';
  status: 'active' | 'inactive' | 'pending';
  adCount: number;
  contactCount: number;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const AdminMembersPage = () => {
  const { hasRole, user } = useAuth();
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedMember, setSelectedMember] = useState<AdminMember | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    role: 'member',
    plan: 'free',
    status: 'active',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [selectedMemberForReset, setSelectedMemberForReset] = useState<AdminMember | null>(null);
  
  // Row-level state tracking
  const [operatingIds, setOperatingIds] = useState<Set<string>>(new Set());
  const [hasLoaded, setHasLoaded] = useState(false);

  // Helper functions to manage operating state
  const addToOperating = (id: string) => {
    setOperatingIds(prev => new Set([...prev, id]));
  };

  const removeFromOperating = (id: string) => {
    setOperatingIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const isOperating = (id: string) => operatingIds.has(id);

  useEffect(() => {
    if (!hasRole(['admin', 'super_admin'])) {
      setMessage({ type: 'error', text: '您需要管理員權限才能訪問此頁面' });
      setIsLoading(false);
      return;
    }
    fetchMembers();
    setHasLoaded(true);
  }, []);

  const fetchMembers = async (isRefresh = false) => {
    // For refresh, don't show loading spinner - just use row-level updates
    if (!isRefresh) {
      setIsLoading(true);
    }
    try {
      const response = await api.get<AdminMember[]>('/api/admin/members');

      if (response.success && response.data) {
        setMembers(response.data);
      } else {
        setMessage({ type: 'error', text: response.error?.message || '載入失敗' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '網路錯誤' });
      console.error('Members fetch error:', err);
    } finally {
      if (!isRefresh) {
        setIsLoading(false);
      }
    }
  };

  const handleEditMember = (member: AdminMember) => {
    setSelectedMember(member);
    setEditForm({
      role: member.role,
      plan: member.plan || 'free',
      status: member.status,
    });
    setShowEditModal(true);
  };

  const handleResetPassword = (member: AdminMember) => {
    setSelectedMemberForReset(member);
    setShowPasswordResetModal(true);
  };

  const handlePasswordResetSuccess = () => {
    setMessage({ type: 'success', text: 'Password reset successfully' });
    setShowPasswordResetModal(false);
    setSelectedMemberForReset(null);
  };

  const handleSaveMember = async () => {
    if (!selectedMember) return;

    // Store original data for rollback
    const originalData = { ...selectedMember };
    
    // Optimistic update - immediately update local state
    setMembers(prev => prev.map(m => 
      m.id === selectedMember.id 
        ? { ...m, role: editForm.role as AdminMember['role'], plan: editForm.plan as AdminMember['plan'], status: editForm.status as AdminMember['status'] }
        : m
    ));
    addToOperating(selectedMember.id);

    try {
      const response = await api.patch(`/api/admin/members/${selectedMember.id}`, editForm);

      if (response.success) {
        setMessage({ type: 'success', text: '會員資料已更新' });
        setShowEditModal(false);
      } else {
        // Rollback on error
        setMembers(prev => prev.map(m => 
          m.id === selectedMember.id ? originalData : m
        ));
        setMessage({ type: 'error', text: response.error?.message || '更新失敗' });
      }
    } catch {
      // Rollback on error
      setMembers(prev => prev.map(m => 
        m.id === selectedMember.id ? originalData : m
      ));
      setMessage({ type: 'error', text: '網路錯誤' });
    } finally {
      removeFromOperating(selectedMember.id);
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      member: '一般會員',
      moderator: '版主',
      admin: '管理員',
      super_admin: '超級管理員',
    };
    return labels[role] || role;
  };

  const getRoleClass = (role: string) => {
    return `role-${role}`;
  };

  // Security: Only allow assigning roles lower than current user's role
  const getAvailableRoles = () => {
    const currentRole = user?.role;
    if (!currentRole) return [];

    const roleHierarchy: Record<string, number> = {
      member: 1,
      moderator: 2,
      admin: 3,
      super_admin: 4,
    };

    const currentLevel = roleHierarchy[currentRole] || 0;

    return Object.entries(roleHierarchy)
      .filter(([_, level]) => level < currentLevel)
      .map(([role]) => role as 'member' | 'moderator' | 'admin' | 'super_admin');
  };

  const getPlanLabel = (plan?: string) => {
    if (!plan) return '-';
    const labels: Record<string, string> = {
      free: '免費',
      basic: '基本',
      premium: '高級',
    };
    return labels[plan] || plan;
  };

  const getStatusClass = (status: string) => {
    return `status-${status}`;
  };

  const filteredMembers = members.filter((member) => {
    const matchesSearch =
      searchTerm === '' ||
      (member.name && member.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.phone && member.phone.includes(searchTerm));

    const matchesRole = roleFilter === 'all' || member.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  return (
    <div className="admin-members-page">
      <DesktopLayout>
      <main className="admin-members-page__main">
        <div className="admin-members-page__container">
          <header className="admin-members-page__header">
            <h1 className="admin-members-page__title">會員管理</h1>
          </header>

      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="alert-close">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="搜尋會員姓名、電話..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">所有角色</option>
          <option value="member">一般會員</option>
          <option value="moderator">版主</option>
          <option value="admin">管理員</option>
          {user?.role === 'super_admin' && <option value="super_admin">超級管理員</option>}
        </select>
      </div>

      {isLoading ? (
        <div className="loading-state">載入中...</div>
      ) : (
        <div className="members-table-container">
          {/* Refresh loading overlay */}
          {hasLoaded && (
            <div className="members-table-refresh-overlay" style={{ display: 'none' }}>
              <div className="spinner spinner--small"></div>
              <span>更新中...</span>
            </div>
          )}
          <table className="members-table">
            <thead>
              <tr>
                <th>姓名</th>
                <th>電子郵件</th>
                <th>電話</th>
                <th>角色</th>
                <th>方案</th>
                <th>狀態</th>
                <th>加入日期</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <tr key={member.id} className={isOperating(member.id) ? 'row--operating' : ''}>
                    <td>{member.name || '-'}</td>
                    <td>{member.email}</td>
                    <td>{member.phone || '-'}</td>
                    <td>
                      <span className={`role-badge ${getRoleClass(member.role)}`}>
                        {getRoleLabel(member.role)}
                      </span>
                    </td>
                    <td>{getPlanLabel(member.plan)}</td>
                    <td>
                      <span className={`content-table__status ${getStatusClass(member.status)}`}>
                        {member.status === 'active' ? '啟用' : member.status === 'inactive' ? '停用' : '待審核'}
                      </span>
                    </td>
                    <td>{new Date(member.createdAt).toLocaleDateString('zh-TW')}</td>
                    <td>
                      <div className="content-table__actions">
                        {isOperating(member.id) ? (
                          <span className="cell-loading">處理中</span>
                        ) : (
                          <>
                            <button
                              className="btn btn--sm btn--primary"
                              onClick={() => handleEditMember(member)}
                              title="編輯"
                            >
                              編輯
                            </button>
                            <button
                              className="btn btn--sm btn--danger"
                              onClick={() => handleResetPassword(member)}
                              title="重置密碼"
                            >
                              重置密碼
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="empty-row">
                    {searchTerm || roleFilter !== 'all' ? '沒有符合條件的會員' : '沒有會員資料'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h2>編輯會員資料</h2>
              <button onClick={() => setShowEditModal(false)} className="modal-close">×</button>
            </div>

            <div className="modal__body">
              <div className="member-info-summary">
                <p><strong>會員：</strong>{selectedMember.name || selectedMember.email}</p>
                <p><strong>電子郵件：</strong>{selectedMember.email}</p>
              </div>

              <div className="form-group">
                <label htmlFor="edit-role">角色</label>
                <select
                  id="edit-role"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'member' | 'moderator' | 'admin' | 'super_admin' })}
                >
                  {getAvailableRoles().map((role) => (
                    <option key={role} value={role}>{getRoleLabel(role)}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="edit-plan">方案</label>
                <select
                  id="edit-plan"
                  value={editForm.plan}
                  onChange={(e) => setEditForm({ ...editForm, plan: e.target.value as 'free' | 'basic' | 'premium' })}
                >
                  <option value="free">免費</option>
                  <option value="basic">基本</option>
                  <option value="premium">高級</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="edit-status">狀態</label>
                <select
                  id="edit-status"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'active' | 'inactive' | 'pending' })}
                >
                  <option value="active">啟用</option>
                  <option value="inactive">停用</option>
                  <option value="pending">待審核</option>
                </select>
              </div>
            </div>

            <div className="modal__footer">
              <button onClick={() => setShowEditModal(false)} className="btn btn--secondary">
                取消
              </button>
              <button onClick={handleSaveMember} className="btn btn--primary">
                儲存變更
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordResetModal && selectedMemberForReset && (
        <PasswordResetModal
          member={{
            id: selectedMemberForReset.id,
            name: selectedMemberForReset.name,
            email: selectedMemberForReset.email,
          }}
          onClose={() => {
            setShowPasswordResetModal(false);
            setSelectedMemberForReset(null);
          }}
          onSuccess={handlePasswordResetSuccess}
        />
      )}
        </div>
      </main>
      </DesktopLayout>
    </div>
  );
};

export default AdminMembersPage;
